// Lifecycle controller for the MCP OAuth consent popup (CIMD / DCR / OAuth2).
//
// The opener (add wizard, reconnect button) opens an authorization URL in a
// popup and must react to EVERY terminal outcome — not just success:
//
//   - consent granted        → the same-origin callback page postMessages
//                              MCP_OAUTH_COMPLETE
//   - cancel/deny at provider
//     or token-exchange fail  → callback page postMessages MCP_OAUTH_ERROR
//                              (best-effort) and self-closes
//   - user closes the window  → detected by polling popup.closed
//
// Correlation: every flow carries a unique `state` (the CSRF token minted by
// the executor /connect call). We match BOTH messages on `state` so a stale,
// leaked listener from an earlier flow cannot cross-talk into this one.
//
// Robustness notes:
//   - The postMessage relay is the PRIMARY signal. The backend callback page
//     posts with targetOrigin "*" and may run on a DIFFERENT origin than this
//     opener (the redirect_uri is the deployed connector domain; the FE may be
//     on localhost), so we accept on the one-shot `state` token + `type`, NOT
//     on event.origin — see onMessage.
//   - popup.closed polling is BEST-EFFORT only: a `Cross-Origin-Opener-Policy:
//     same-origin` header on the AS authorize page can sever the opener's
//     handle so `closed` reads stale while the user is still on the provider.
//     It reliably catches a manual close of OUR same-origin pages (callback /
//     blank), which is the common "I changed my mind" path.
//   - `settled` guards against double-firing when a success message and the
//     subsequent self-close race. Whichever lands first wins; the rest no-op.

export const MCP_OAUTH_COMPLETE = "MCP_OAUTH_COMPLETE";
export const MCP_OAUTH_ERROR = "MCP_OAUTH_ERROR";

// BroadcastChannel name the callback page and every opener share to relay the
// OAuth result SAME-ORIGIN. This is the fallback for providers whose authorize
// page sets Cross-Origin-Opener-Policy: same-origin (HuggingFace, Notion, …),
// which severs window.opener and kills the postMessage path. COOP does not affect
// BroadcastChannel, so a same-origin opener (FE + callback both on connector.<env>)
// still receives the result. KEEP IN SYNC with api-connector api/mcp.go
// mcpCallbackHTML (the `new BroadcastChannel("mcp-oauth")` literal).
const MCP_OAUTH_CHANNEL = "mcp-oauth";

// NOTE: the displayed "Callback URL" (the redirect_uri users register in a
// pre-registered OAuth app) is no longer computed here — it is fetched from the
// backend via getMcpOAuthCallbackUrl (services/mcp-service), whose single source
// of truth is the executor's oauth.CIMDRedirectURI(). This file owns only the live
// OAuth popup runtime (open tab + watch result), which uses the backend-provided
// authorization_url + state, never the displayed redirect_uri.

// Window/tab name reused for every MCP OAuth consent, so a retry reuses the same
// tab instead of piling up new ones.
const MCP_OAUTH_WINDOW_NAME = "mcp-oauth";

/**
 * Open the OAuth consent URL in a NEW TAB (not a popup window — no width/height
 * features). SHARED by the add wizard AND the manage-page reconnect so both
 * flows open consent identically and cannot drift. Returns the tab handle to
 * hand to watchMcpOAuthPopup, or null if the browser blocked it (the caller
 * shows its own popup-blocked message). The same-origin callback page
 * postMessages the result back and self-closes the tab (window.opener +
 * window.close both work for a script-opened tab).
 */
export function openMcpOAuthTab(authorizationUrl: string): Window | null {
  if (typeof window === "undefined") return null;
  return window.open(authorizationUrl, MCP_OAUTH_WINDOW_NAME);
}

const CLOSE_POLL_INTERVAL_MS = 500;
// After detecting popup.closed, wait one tick so a just-posted success/error
// message (already queued) is delivered before we fall back to "cancelled".
const CLOSE_SETTLE_GRACE_MS = 300;
// COOP severance window. A provider whose authorize page sets COOP: same-origin
// detaches our popup handle the moment the popup navigates there, and a detached
// handle reports `closed === true` WHILE the user is still consenting. Treat a
// close detected within this window of opening as that severance (not a user
// dismiss): stop polling and let the BroadcastChannel/postMessage success or the
// wall-clock timeout settle, instead of firing a false cancel. A genuine
// same-origin close happens well after this.
const COOP_SEVERANCE_GUARD_MS = 2000;
// Wall-clock backstop. If the user wanders off without granting consent OR
// closing the consent tab, popup.closed polling never fires (and a provider
// COOP header can freeze the handle entirely), so the opener's "waiting" state
// would hang forever. Abandon the flow after this long. Callers may override via
// handlers.timeoutMs. 90s: with the BroadcastChannel relay now delivering success
// reliably same-origin, this is mainly the abandon-fallback — kept short for a
// snappier give-up. Trade-off: on a COOP provider (where popup.closed can't tell
// active-consent from abandoned) a consent that genuinely runs past 90s is
// false-cancelled; raise this if real flows need longer.
const OAUTH_FLOW_TIMEOUT_MS = 90 * 1000;

export interface WatchMcpOAuthPopupHandlers {
  /** Correlation id from connectMCP's needs_oauth response. */
  state: string;
  /** Consent granted + token exchanged. `systemId` echoes the connected mcp_id. */
  onComplete: (systemId: string) => void;
  /** Provider denied/cancelled, or token exchange failed.
   * `error` is the structured code (Flow B or legacy coerced); `legacyMessage` is
   * the raw `message` field from older callback pages — passed as fallback only. */
  onError: (error: string, legacyMessage: string | undefined) => void;
  /** Popup closed without ever settling (user dismissed it). */
  onCancel: () => void;
  /**
   * Wall-clock timeout (ms) after which an unfinished flow is abandoned: onCancel
   * fires and the consent tab is nudged shut (best-effort). Guards against a hung
   * "waiting" state when the user neither completes nor closes the tab. Defaults
   * to OAUTH_FLOW_TIMEOUT_MS (90s).
   */
  timeoutMs?: number;
}

interface OAuthPopupMessage {
  type?: string;
  state?: string;
  system_id?: string;
  /** Legacy field — kept for rollout back-compat with older callback pages. */
  message?: string;
  /** Flow B error code emitted by api-ai-transform/api/mcp.go (AD-4). */
  error?: string;
  /** Optional secondary provider detail — never used as primary copy (AD-4). */
  error_description?: string;
}

/**
 * Wire up message + close-poll listeners for an opened OAuth popup. Returns a
 * disposer that removes the listener and stops polling — call it on component
 * unmount so a mid-flight flow cannot leak. Exactly one of onComplete /
 * onError / onCancel fires per popup.
 */
export function watchMcpOAuthPopup(
  popup: Window,
  handlers: WatchMcpOAuthPopupHandlers,
): () => void {
  let settled = false;
  let pollId: ReturnType<typeof setInterval> | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  // COOP-immune same-origin relay (see MCP_OAUTH_CHANNEL). null when the browser
  // lacks BroadcastChannel — the postMessage + close-poll paths below still apply.
  const channel =
    typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel(MCP_OAUTH_CHANNEL)
      : null;
  const openedAt = Date.now();

  const stopPoll = () => {
    if (pollId !== null) {
      clearInterval(pollId);
      pollId = null;
    }
  };

  const dispose = () => {
    stopPoll();
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    window.removeEventListener("message", onMessage);
    channel?.close();
  };

  const settle = (run: () => void) => {
    if (settled) return;
    settled = true;
    dispose();
    run();
  };

  function onMessage(event: MessageEvent) {
    const data = event.data as OAuthPopupMessage | undefined;
    // SECURITY / CORRELATION: validate by the one-shot `state` (a high-entropy
    // CSRF token the executor mints at /connect, known only to the legitimate
    // callback) and our own `type`, NOT by event.origin. The backend callback
    // (HandleMCPOAuthCallbackRedirect → mcpCallbackHTML) runs on the DEPLOYED
    // connector domain baked into the OAuth redirect_uri, which is a DIFFERENT
    // origin from this opener whenever the FE runs on localhost (or any host
    // other than that domain). It posts with targetOrigin "*" and expects the
    // opener to accept on `state` + `type`; gating on event.origin here silently
    // dropped EVERY cross-origin completion (add wizard never navigated,
    // reconnect never revalidated). Matches the ChatGPT/Claude.ai connector
    // popup contract. A leaked listener / spoof with the wrong `state` is ignored.
    if (!data || data.state !== handlers.state) return;
    if (data.type === MCP_OAUTH_COMPLETE) {
      settle(() => handlers.onComplete(data.system_id ?? ""));
    } else if (data.type === MCP_OAUTH_ERROR) {
      // Pass error code + legacy message separately so the hook can call
      // messageForMcpCode(error, legacyMessage, tMcp) — keeping provider
      // error_description out of the fallback slot (AD-4).
      settle(() =>
        handlers.onError(
          data.error ?? "",
          data.message,
        ),
      );
    }
  }

  window.addEventListener("message", onMessage);
  // Same handler, COOP-immune path: BroadcastChannel delivers a MessageEvent whose
  // .data is the posted result, identical in shape to the window "message" event,
  // so the `state` / `type` correlation in onMessage applies unchanged.
  channel?.addEventListener("message", onMessage);

  pollId = setInterval(() => {
    if (!popup.closed) return;
    stopPoll();
    // A COOP-severed handle reports closed===true while consent is still open.
    // An implausibly-early close is that severance, not a user dismiss: we already
    // stopped polling above; do NOT cancel — let the relay / wall-clock timeout
    // settle instead (the actual fix for provider-COOP false cancels).
    if (Date.now() - openedAt < COOP_SEVERANCE_GUARD_MS) return;
    // Grace window: let an in-flight success/error message settle first.
    window.setTimeout(() => settle(handlers.onCancel), CLOSE_SETTLE_GRACE_MS);
  }, CLOSE_POLL_INTERVAL_MS);

  // Wall-clock backstop so the opener never hangs on an abandoned flow.
  timeoutId = setTimeout(() => {
    // Best-effort: nudge the lingering consent tab shut. A script-opened tab can
    // be closed by its opener even cross-origin; a COOP-severed handle no-ops.
    try {
      popup.close();
    } catch {
      /* handle detached by COOP — nothing to close */
    }
    settle(handlers.onCancel);
  }, handlers.timeoutMs ?? OAUTH_FLOW_TIMEOUT_MS);

  return dispose;
}
